if (!customElements.get('cookie-banner')) {
  customElements.define(
    'cookie-banner',
    class CookieBanner extends ModalElement {
      constructor() {
        super();
    
        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }
    
      get acceptButton() {
        return this.querySelector('button[name="accept"]');
      }
    
      get declineButton() {
        return this.querySelector('button[name="decline"]');
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;
    
        if (this.testMode) {
          this.load(this.delay);
          return;
        }
    
        window.Shopify.loadFeatures([{
          name: 'consent-tracking-api',
          version: '0.1',
          onLoad: this.onConsentLibraryLoaded.bind(this)
        }]);
      }
    
      load(delay) {
        if (Shopify.designMode) return;
    
        setTimeout(() => this.show(), delay * 1000);
    
        if (this.acceptButton) this.acceptButton.addEventListener('click', this.acceptPolicy.bind(this));
        if (this.declineButton) this.declineButton.addEventListener('click', this.declinePolicy.bind(this));
      }
    
      acceptPolicy() {
        this.hide();

        // Granular object form (analytics / marketing / preferences) is the
        // current Customer Privacy API signature; the legacy boolean shorthand
        // is still accepted but says nothing about which category was granted.
        // https://shopify.dev/docs/api/customer-privacy
        window.Shopify.customerPrivacy?.setTrackingConsent(
          { analytics: true, marketing: true, preferences: true },
          this.notifyAnalytics
        );
      }

      declinePolicy() {
        this.hide();

        window.Shopify.customerPrivacy?.setTrackingConsent(
          { analytics: false, marketing: false, preferences: false },
          this.noop
        );
      }

      // Hands control back to snippets/drlh-analytics.liquid so Rybbit and the
      // Meta Pixel load the moment consent is granted. Shopify also dispatches
      // `visitorConsentCollected`, which that snippet listens for; this direct
      // call is the belt to that braces.
      notifyAnalytics() {
        window.drlhAnalytics?.consentUpdated();
      }

      onConsentLibraryLoaded() {
        const privacy = window.Shopify.customerPrivacy;
        if (!privacy) return;

        // `shouldShowBanner()` returns true only in regions the merchant has
        // configured for consent collection under Settings > Customer privacy.
        // This storefront serves a US audience, where no law requires a cookie
        // banner, so we defer entirely to that region config rather than
        // showing one to everybody. Configure a region in admin and the banner
        // appears there automatically - no code change needed.
        //
        // Outside a configured region Shopify's own API reports consent as
        // allowed, so `drlh-analytics.liquid` still loads its trackers for US
        // visitors. The gate there is what keeps the two consistent: if a
        // region IS configured later, nothing loads until the visitor accepts.
        const shouldShowBanner = typeof privacy.shouldShowBanner === 'function'
          ? privacy.shouldShowBanner()
          : !privacy.userCanBeTracked();

        if (shouldShowBanner && !this.hasAnsweredConsent(privacy)) {
          this.load(this.delay);
        }
      }

      // `currentVisitorConsent()` returns per-category strings: 'yes', 'no', or
      // '' when the visitor has not been asked. Any non-empty value counts as
      // an answer, so the banner is not shown twice.
      hasAnsweredConsent(privacy) {
        if (typeof privacy.currentVisitorConsent !== 'function') return false;

        const consent = privacy.currentVisitorConsent() || {};

        return Object.keys(consent).some((key) => {
          const value = consent[key];
          return value === 'yes' || value === 'no';
        });
      }
    
      noop() {
      }
    
      afterHide() {
        super.afterHide();
        document.body.classList.remove('has-cookie-banner');
      }
      afterShow() {
        super.afterShow();
        document.body.classList.add('has-cookie-banner');
      }
    }
  );
}
